// Single source of truth for the subscription plan prices (CLAUDE.md §7) and
// the billing-period discounts. Consumed by the pricing-page grid AND the
// landing-page JSON-LD offers (and anywhere else a price is shown) so a price
// change is ONE edit here instead of drifting across files — same rationale as
// data/modelStats.js for the model count.
//
// Monthly price in EUR per plan id.
export const PLAN_PRICES = {
  free: 0,
  starter: 9,
  pro: 19,
  business: 39,
  enterprise: 59,
};

// Fractional discount applied to the equivalent monthly total when paying for a
// longer period (quarterly −10 %, yearly −30 %). Kept here so the pricing grid
// and any marketing copy compute the same numbers.
export const BILLING_DISCOUNTS = {
  monthly: 0,
  quarterly: 0.1,
  yearly: 0.3,
};

// Free-trial length in days per billing period, mirroring the LemonSqueezy
// variant trial config (monthly/quarterly = 7d, yearly = 14d — a deliberate
// nudge to annual). Single source so the pricing badge + FAQ + JSON-LD all
// state the same number; changing the trial is ONE edit here (plus the matching
// change on LemonSqueezy). Interpolated into copy via {{days}}/{{yearlyDays}}.
export const TRIAL_DAYS = {
  default: 7, // monthly + quarterly
  yearly: 14,
};

// Affiliate program terms (CLAUDE.md §7, mirrors the backend affiliate logic).
// The partner-facing "worked examples" commission table on /affiliates is
// DERIVED from these + PLAN_PRICES/BILLING_DISCOUNTS (see affiliateCommissionEUR
// below) so the € amounts can't silently drift when a price or discount
// changes — the same single-source rationale as PLAN_PRICES itself.
export const AFFILIATE = {
  RATE: 0.3, // 30 % of the amount the referred customer pays
  COMMISSION_MONTHS: 12, // paid recurring for the first 12 months
  COOKIE_DAYS: 60, // click-to-signup attribution window
  PAYOUT_THRESHOLD_EUR: 50, // minimum balance before a payout is issued
};

// Commission (EUR) a partner earns over AFFILIATE.COMMISSION_MONTHS for one
// referred signup on `plan` billed over `period` (monthly/quarterly/yearly):
// discounted monthly price × months × rate. Returns a Number; format at the
// call site (e.g. `€${affiliateCommissionEUR("pro","yearly").toFixed(2)}`).
export function affiliateCommissionEUR(plan, period) {
  const discountedMonthly = PLAN_PRICES[plan] * (1 - BILLING_DISCOUNTS[period]);
  return discountedMonthly * AFFILIATE.COMMISSION_MONTHS * AFFILIATE.RATE;
}

// Plan tiers in display order, each pointing at the one it builds on.
export const PLAN_TIERS = [
  { id: "free", builds_on: null },
  { id: "starter", builds_on: "free" },
  { id: "pro", builds_on: "starter" },
  { id: "business", builds_on: "pro" },
  { id: "enterprise", builds_on: "business" },
];
